import { useState, useEffect } from "react";

type Client = {
  id: string;
  tenant_id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  status: string;
  created_at: string;
  updated_at: string;
};

interface Props {
  client: Client | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (data: Client) => void;
}

export default function ClientModal({ client, isNew, onClose, onSave }: Props) {
  const [form, setForm] = useState<Client>({
    id: "",
    tenant_id: "",
    full_name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    status: "active",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  useEffect(() => {
    if (client) setForm(client);
  }, [client]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...form, updated_at: new Date().toISOString() });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-[#0f172a] font-semibold text-base">
            {isNew ? "New Client" : "Edit Client"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block">Full Name *</label>
              <input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all"
                placeholder="Client or couple name"
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block">Email *</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all"
                placeholder="+1 (555) 000-0000"
              />
            </div>
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block">Address</label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all"
                placeholder="Street, City, State, ZIP"
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#C8A96E]/60 transition-all cursor-pointer"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block">Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all resize-none"
                placeholder="Internal notes about this client..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? "Create Client" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
